import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuthPayload, getUserEmail } from '../utils/auth';
import { getDisplayName } from '../utils/userProfiles';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const ROLE_OPTIONS = ['Super Admin', 'Grant Manager', 'Internal Reviewer'];
const SUPER_ADMIN_ROLE = 'Super Admin';

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  roles: ['Grant Manager'],
};

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function RoleCheckboxes({ value, onChange, namePrefix }) {
  const selectedRoles = value || [];

  return (
    <div className="user-role-grid">
      {ROLE_OPTIONS.map((role) => (
        <label key={`${namePrefix}-${role}`} className="compact-checkbox">
          <input
            type="checkbox"
            checked={selectedRoles.includes(role)}
            onChange={(event) => {
              if (event.target.checked) {
                onChange([...selectedRoles, role]);
              } else {
                onChange(selectedRoles.filter((item) => item !== role));
              }
            }}
          />
          <span>{role}</span>
        </label>
      ))}
    </div>
  );
}

const UserManagementPage = () => {
  const token = localStorage.getItem('token');
  const payload = getAuthPayload();
  const currentUserEmail = getUserEmail(payload);
  const currentUserRoles = Array.isArray(payload?.roles) ? payload.roles : [payload?.role].filter(Boolean);
  const canCreateUsers = currentUserRoles.includes(SUPER_ADMIN_ROLE);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [editingUserId, setEditingUserId] = useState('');
  const [editingRoles, setEditingRoles] = useState([]);
  const [resetUserId, setResetUserId] = useState('');
  const [resetPassword, setResetPassword] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.message || 'Unable to load users.');
      }
      setUsers(body);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load users.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadUsers();
    }
  }, [token, loadUsers]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) =>
      getDisplayName(user).toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      (user.roles || []).join(' ').toLowerCase().includes(query)
    );
  }, [search, users]);

  const showMessage = (text) => {
    setMessage(text);
    window.setTimeout(() => setMessage(''), 2400);
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setBusyAction('create');
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(createForm),
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.message || 'Unable to create user.');
      }

      setUsers((current) => [...current, body].sort((left, right) => left.email.localeCompare(right.email)));
      setCreateForm(EMPTY_FORM);
      setShowCreateModal(false);
      showMessage('User created.');
    } catch (createError) {
      setError(createError.message || 'Unable to create user.');
    } finally {
      setBusyAction('');
    }
  };

  const handleSaveRoles = async (userId) => {
    setBusyAction(`roles-${userId}`);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ roles: editingRoles }),
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.message || 'Unable to update roles.');
      }

      setUsers((current) => current.map((user) => (user.id === userId ? body : user)));
      setEditingUserId('');
      setEditingRoles([]);
      showMessage('Roles updated.');
    } catch (updateError) {
      setError(updateError.message || 'Unable to update roles.');
    } finally {
      setBusyAction('');
    }
  };

  const handleResetPassword = async (userId) => {
    setBusyAction(`reset-${userId}`);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ password: resetPassword }),
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.message || 'Unable to reset password.');
      }

      setResetUserId('');
      setResetPassword('');
      showMessage('Password reset.');
    } catch (resetError) {
      setError(resetError.message || 'Unable to reset password.');
    } finally {
      setBusyAction('');
    }
  };

  return (
    <div className="page-stack">
      <section className="dashboard-section">
        {message && <div className="success-banner">{message}</div>}
        {error && <div className="error-banner">{error}</div>}

        <div className="workspace-two-column user-management-overview">
          <div className="workspace-panel user-management-create-panel">
            <div className="workspace-panel-top">
              <div>
                <h3>Account Actions</h3>
                <p>Create internal reviewer accounts and assign access only when needed.</p>
              </div>
            </div>
            <div className="user-management-action-copy">
              <strong>Add a user when you need to onboard a new reviewer, manager, or administrator.</strong>
              <p>The account form opens in a separate dialog so the directory stays easy to scan.</p>
            </div>
            <div className="section-actions">
              {canCreateUsers && (
                <button
                  className="add-application-btn"
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create User
                </button>
              )}
            </div>
          </div>

          <div className="workspace-panel">
            <div className="workspace-panel-top">
              <div>
                <h3>Directory</h3>
                <p>Search current internal accounts and review assigned roles.</p>
              </div>
            </div>
            <div className="filter-group">
              <label className="filter-label">Search</label>
              <input
                className="search-input"
                placeholder="Filter by email or role"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="workspace-summary-card">
              <strong>{users.length}</strong>
              <span>Total users</span>
            </div>
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-top">
          <div>
            <h2>Users</h2>
          </div>
        </div>

        {loading ? (
          <p>Loading users…</p>
        ) : (
          <div className="table-card user-directory-card">
            <table className="user-directory-table">
              <colgroup>
                <col className="user-col-email" />
                <col className="user-col-email" />
                <col className="user-col-roles" />
                <col className="user-col-created" />
                <col className="user-col-updated" />
                <col className="user-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const isEditing = editingUserId === user.id;
                  const isResetting = resetUserId === user.id;
                  return (
                    <tr key={user.id}>
                      <td data-label="Name">
                        <div className="table-primary-cell">
                          <strong>{getDisplayName(user)}</strong>
                        </div>
                      </td>
                      <td data-label="Email">
                        <div className="table-primary-cell">
                          <span>{user.email}</span>
                          {user.email === currentUserEmail && <span className="status-badge neutral">You</span>}
                        </div>
                      </td>
                      <td data-label="Roles">
                        {isEditing ? (
                          <RoleCheckboxes
                            value={editingRoles}
                            onChange={setEditingRoles}
                            namePrefix={`edit-${user.id}`}
                          />
                        ) : (
                          <div className="table-chip-row">
                            {(user.roles || []).map((role) => (
                              <span key={role} className="status-badge neutral">{role}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td data-label="Created">{formatDateTime(user.createdAt)}</td>
                      <td data-label="Updated">{formatDateTime(user.updatedAt)}</td>
                      <td data-label="Actions">
                        <div className="table-actions user-table-actions">
                          {isEditing ? (
                            <>
                              <button
                                className="view-all-btn"
                                type="button"
                                onClick={() => handleSaveRoles(user.id)}
                                disabled={busyAction === `roles-${user.id}`}
                              >
                                Save Roles
                              </button>
                              <button
                                className="secondary-btn"
                                type="button"
                                onClick={() => {
                                  setEditingUserId('');
                                  setEditingRoles([]);
                                }}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              className="view-all-btn"
                              type="button"
                              onClick={() => {
                                setEditingUserId(user.id);
                                setEditingRoles(user.roles || []);
                              }}
                            >
                              Edit Roles
                            </button>
                          )}

                          {isResetting ? (
                            <div className="inline-reset-form">
                              <input
                                type="text"
                                placeholder="New password"
                                minLength={8}
                                value={resetPassword}
                                onChange={(event) => setResetPassword(event.target.value)}
                              />
                              <button
                                className="secondary-btn"
                                type="button"
                                onClick={() => handleResetPassword(user.id)}
                                disabled={busyAction === `reset-${user.id}`}
                              >
                                Save
                              </button>
                              <button
                                className="secondary-btn"
                                type="button"
                                onClick={() => {
                                  setResetUserId('');
                                  setResetPassword('');
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              className="secondary-btn"
                              type="button"
                              onClick={() => {
                                setResetUserId(user.id);
                                setResetPassword('');
                              }}
                            >
                              Reset Password
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan="6">
                      <div className="workspace-empty-state">
                        <strong>No users found</strong>
                        <p>Try a different search or create a new account.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {canCreateUsers && showCreateModal && (
        <div className="user-management-modal-overlay" role="presentation" onClick={() => setShowCreateModal(false)}>
          <div
            className="user-management-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-user-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="workspace-panel-top">
              <div>
                <h3 id="create-user-dialog-title">Create User</h3>
                <p>Add a new internal user and assign their starting roles.</p>
              </div>
            </div>

            <form onSubmit={handleCreateUser} noValidate>
              <div className="form-row">
                <div className="form-field">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={createForm.firstName}
                    onChange={(event) => setCreateForm((current) => ({ ...current, firstName: event.target.value }))}
                  />
                </div>
                <div className="form-field">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={createForm.lastName}
                    onChange={(event) => setCreateForm((current) => ({ ...current, lastName: event.target.value }))}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(event) => setCreateForm((current) => ({ ...current, email: event.target.value }))}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Temporary Password</label>
                  <input
                    type="text"
                    value={createForm.password}
                    onChange={(event) => setCreateForm((current) => ({ ...current, password: event.target.value }))}
                    minLength={8}
                    required
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Roles</label>
                <RoleCheckboxes
                  value={createForm.roles}
                  onChange={(roles) => setCreateForm((current) => ({ ...current, roles }))}
                  namePrefix="create-user-role"
                />
              </div>

              <div className="programs-modal-actions">
                <button
                  className="secondary-btn"
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateForm(EMPTY_FORM);
                  }}
                >
                  Cancel
                </button>
                <button className="add-application-btn" type="submit" disabled={busyAction === 'create'}>
                  {busyAction === 'create' ? 'Creating…' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
